import { useEffect, useState, type FormEvent, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, CloseIcon, DownloadIcon, EditIcon, ErrorState, ExternalIcon, LoadingState, Modal, PlusIcon, Segments, Select, TrashIcon, useToast } from "@signet/ui";
import { folderUrl, KINDS, UNITS, type Backfill, type Kind, type Unit } from "../shared/downloads.ts";
import { checkFolder, downloadKeys, fetchDownloads, saveDownloads, type Folder } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Downloads on the page: the cell on each creator's row, and the popup where
   their Drive folders are set up (Jay, 2026-10-15). A folder shows by name,
   linked to Drive; its link only shows while it is being edited.
   ────────────────────────────────────────────────────────────────────────── */

const stop = (event: MouseEvent) => event.stopPropagation();

/**
 * "Setup Auto-Downloads" until a creator has a folder, then "Auto Download Active" with a pencil on hover (Ben, 2026-10-29).
 * A creator ticked as uploading their own content says so instead, so the row doesn't look forgotten (Ben, 2026-10-29).
 * It reads "Auto Download turned off" in red (Jay, 2026-11-04): "Creator uploads content" was too long to sit beside
 * Re-Download and pushed it onto a second line.
 */
export function DownloadCell({ folders, creatorUploads, canEdit, onOpen }: { folders: number; creatorUploads: boolean; canEdit: boolean; onOpen: () => void }) {
  if (folders === 0 && creatorUploads) {
    return (
      <span className="ma-dl own" onClick={stop}>
        <CloseIcon size={13} /> Auto Download turned off
        {canEdit ? (
          <button type="button" className="icon-button ma-dl-edit" aria-label="Edit downloads" onClick={onOpen}>
            <EditIcon size={13} />
          </button>
        ) : null}
      </span>
    );
  }
  if (folders > 0) {
    return (
      <span className="ma-dl set" onClick={stop}>
        <DownloadIcon size={13} /> Auto Download Active
        {canEdit ? (
          <button type="button" className="icon-button ma-dl-edit" aria-label="Edit downloads" onClick={onOpen}>
            <EditIcon size={13} />
          </button>
        ) : null}
      </span>
    );
  }
  if (!canEdit) return <span />;
  return (
    <span onClick={stop}>
      <button type="button" className="secondary-button ma-dl-setup" onClick={onOpen}>
        <DownloadIcon size={13} /> Setup Auto-Downloads
      </button>
    </span>
  );
}

type Line = {
  key: string;
  id?: string;
  folderId: string;
  folderName: string;
  kind: Kind;
  accountId: string | null;
  /** The link box is open: a new line before its link is checked, or a folder being changed. */
  editing: boolean;
  link: string;
  checking: boolean;
  error: string | null;
  sent?: number;
  queued?: number;
  failed?: number;
};

const ALL = "__all";
let seq = 0;
const lineOf = (f: Folder): Line => ({ key: f.id, id: f.id, folderId: f.folderId, folderName: f.folderName, kind: f.kind, accountId: f.accountId, editing: false, link: "", checking: false, error: null, sent: f.sent, queued: f.queued, failed: f.failed });
const blank = (): Line => ({ key: `new-${++seq}`, folderId: "", folderName: "", kind: "all", accountId: null, editing: true, link: "", checking: false, error: null });

function Progress({ line }: { line: Line }) {
  const bits = [line.sent ? `${line.sent.toLocaleString()} sent` : null, line.queued ? `${line.queued.toLocaleString()} waiting` : null, line.failed ? `${line.failed.toLocaleString()} failed` : null].filter(Boolean);
  return bits.length ? <span className="ma-dl-progress">{bits.join(", ")}</span> : null;
}

export function DownloadsModal({ creator, open, onClose }: { creator: string; open: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const data = useQuery({ queryKey: downloadKeys.creator(creator), queryFn: () => fetchDownloads(creator), enabled: open });
  const [lines, setLines] = useState<Line[] | null>(null);
  const [previous, setPrevious] = useState(false);
  const [own, setOwn] = useState(false);
  const [everything, setEverything] = useState(false);
  const [amount, setAmount] = useState("2");
  const [unit, setUnit] = useState<Unit>("months");

  // Filled from the server each time the box opens, so a closed box never keeps half-made changes.
  useEffect(() => {
    if (!open) {
      setLines(null);
      return;
    }
    if (data.data && lines === null) {
      setLines(data.data.folders.length ? data.data.folders.map(lineOf) : [blank()]);
      setOwn(data.data.creatorUploads);
    }
  }, [open, data.data, lines]);

  const firstSetup = (data.data?.folders.length ?? 0) === 0;
  // A creator who uploads their own content and has no folders yet needs none: the folder lines step aside.
  const ownOnly = own && firstSetup;
  const accounts = data.data?.accounts ?? [];
  const update = (key: string, patch: Partial<Line>) => setLines((all) => (all ?? []).map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const check = async (line: Line, event?: FormEvent) => {
    event?.preventDefault();
    if (!line.link.trim()) return;
    update(line.key, { checking: true, error: null });
    try {
      const folder = await checkFolder(line.link);
      update(line.key, { folderId: folder.id, folderName: folder.name, editing: false, checking: false, link: "" });
    } catch (error) {
      update(line.key, { checking: false, error: (error as Error).message });
    }
  };

  const cancelEdit = (line: Line) => (line.folderId ? update(line.key, { editing: false, link: "", error: null }) : setLines((all) => (all ?? []).filter((l) => l.key !== line.key)));

  const ready = ownOnly ? [] : (lines ?? []).filter((l) => l.folderId);
  const unsure = !ownOnly && (lines ?? []).some((l) => l.editing && l.link.trim());
  const count = Number(amount);
  const backfill: Backfill | null = ownOnly || !firstSetup || !previous ? null : everything ? { all: true } : { amount: count, unit };
  const backfillOk = ownOnly || !previous || everything || (Number.isInteger(count) && count >= 1 && count <= 999);

  const save = useMutation({
    mutationFn: () => saveDownloads(creator, ready.map((l) => ({ id: l.id, folderId: l.folderId, kind: l.kind, accountId: l.accountId })), backfill, own),
    onSuccess: (r) => {
      client.invalidateQueries({ queryKey: downloadKeys.all });
      const what = ready.length ? `${ready.length} ${ready.length === 1 ? "folder" : "folders"} for ${creator}.` : own ? `${creator} uploads their own content.` : `No folders for ${creator} now.`;
      toast.success("Auto-Downloads saved", r.backfills ? `${what} Previous posts are being read now.` : what);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't save the Auto-Downloads", error.message),
  });

  const accountOptions = [{ value: ALL, label: "All accounts" }, ...accounts.map((a) => ({ value: a.id, label: `@${a.handle}` }))];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title={`Auto-Downloads for ${creator}`}
      description="Where this creator's posts are saved in Google Drive. Each folder must be shared with jayxoga@gmail.com as an Editor."
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!lines || unsure || !backfillOk || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving" : "Save"}
          </button>
        </>
      }
    >
      {data.isLoading || (open && !lines && !data.isError) ? (
        <LoadingState />
      ) : data.isError ? (
        <ErrorState title="Couldn't load the downloads" onRetry={() => data.refetch()} />
      ) : (
        <div className="ma-dl-box">
          <label className="ma-dl-check ma-dl-own">
            <Checkbox checked={own} onChange={setOwn} label="Creator uploads content" />
            <span>Creator uploads content</span>
          </label>

          {ownOnly ? null : (lines ?? []).map((line) => (
            <div key={line.key} className="ma-dl-line">
              {line.editing ? (
                <form className="ma-dl-link" onSubmit={(e) => check(line, e)}>
                  <input
                    value={line.link}
                    onChange={(e) => update(line.key, { link: e.target.value, error: null })}
                    placeholder="Paste a Google Drive folder link"
                    aria-label="Google Drive folder link"
                    autoFocus
                  />
                  <button type="submit" className="secondary-button" disabled={!line.link.trim() || line.checking}>
                    {line.checking ? "Checking" : line.folderId ? "Change" : "Add"}
                  </button>
                  <button type="button" className="text-button" onClick={() => cancelEdit(line)}>
                    Cancel
                  </button>
                  {line.error ? <p className="ma-dl-error">{line.error}</p> : null}
                </form>
              ) : (
                <>
                  <a className="ma-dl-folder" href={folderUrl(line.folderId)} target="_blank" rel="noreferrer noopener" title="Open the folder in Google Drive">
                    <span>{line.folderName}</span>
                    <ExternalIcon size={12} />
                  </a>
                  <Progress line={line} />
                  <Segments label="What to save" value={line.kind} options={KINDS.map((k) => ({ value: k.id, label: k.label }))} onChange={(kind) => update(line.key, { kind })} />
                  <Select
                    label="Which accounts"
                    compact
                    value={line.accountId ?? ALL}
                    options={accountOptions}
                    onChange={(v) => update(line.key, { accountId: !v || v === ALL ? null : v })}
                  />
                  <span className="ma-dl-actions">
                    <button type="button" className="icon-button" aria-label={`Edit the link to ${line.folderName}`} onClick={() => update(line.key, { editing: true, link: folderUrl(line.folderId) })}>
                      <EditIcon size={14} />
                    </button>
                    <button type="button" className="icon-button" aria-label={`Delete ${line.folderName}`} onClick={() => setLines((all) => (all ?? []).filter((l) => l.key !== line.key))}>
                      <TrashIcon size={14} />
                    </button>
                  </span>
                </>
              )}
            </div>
          ))}

          {ownOnly ? null : (
            <button type="button" className="secondary-button ma-dl-add" onClick={() => setLines((all) => [...(all ?? []), blank()])}>
              <PlusIcon size={14} /> Add folder
            </button>
          )}

          {/* Asked once, when a creator's downloads are first set up (Jay, 2026-10-15). */}
          {firstSetup && !ownOnly ? (
            <div className="ma-dl-previous">
              <label className="ma-dl-check">
                <Checkbox checked={previous} onChange={setPrevious} label="Download previous posts?" />
                <span>Download previous posts?</span>
              </label>
              {previous ? (
                <div className="ma-dl-since">
                  <span className={everything ? "off" : ""}>Past</span>
                  <input
                    className="ma-dl-amount"
                    inputMode="numeric"
                    value={amount}
                    disabled={everything}
                    onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    aria-label="How many"
                  />
                  <Select label="Days, weeks or months" compact className="ma-dl-unit" disabled={everything} value={unit} options={UNITS.map((u) => ({ value: u.id, label: u.label }))} onChange={(v) => v && setUnit(v)} />
                  <label className="ma-dl-check">
                    <Checkbox checked={everything} onChange={setEverything} label="All previous posts" />
                    <span>All</span>
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
